/**
 * The seam between a database row and the objects this product already knows how to think about.
 *
 * WHY THIS FILE EXISTS RATHER THAN A REWRITE
 *
 * Every domain module in this repo — the stage machine, the pricing engine, the hold arithmetic,
 * the automation engine — is a pure function over plain objects, and 778 tests hold those shapes
 * still. The database speaks a different dialect: snake_case, integer cents, uuids where the
 * product says `MTR-2048`, and one table per relationship where the product carries a nested list.
 *
 * There were two ways to bridge that. Change the domain to speak the database's dialect, and throw
 * away the protection of every one of those tests on the same afternoon the product stops being a
 * prototype. Or write the translation down once, in a file with nothing else in it, and leave the
 * domain exactly as proved. This is the second.
 *
 * THREE RULES THIS FILE KEEPS
 *
 * 1. IT IS PURE. No fetch, no client, no clock. Every function here is `row in, object out`, which
 *    is why it can be tested without a network and why a mapping mistake is found by a test rather
 *    than by Yuliana.
 *
 * 2. MONEY CROSSES HERE AND NOWHERE ELSE. The database stores integer cents in every column whose
 *    name ends `_cents`. The product is inconsistent by history — a hold carries `totalCents`, a
 *    lead carries `budgetMonthly` in whole dollars, a home's settings carry `monthlyRent` in whole
 *    dollars. Rather than "fix" the product and break its tests, both dialects are converted at
 *    this one border, and every conversion is named so it can be read.
 *
 * 3. AN ID IS A UUID NOW, AND THE HUMAN NAME LIVES BESIDE IT. `MTR-2048` is what people say out
 *    loud, so it survives as `reference`. The identity of a home is the canonical uuid, not
 *    Guesty's — but `guestyId` rides along on every unit, because the morning something is wrong
 *    somebody has to open Guesty and find the same home.
 */

import { upgradeFilters } from '../automation/filterUpgrade.js'
import { RETIRING_STAGES } from '../leads/stages.js'

/* ── money ────────────────────────────────────────────────────────────────────────────────── */

/** Integer cents → whole dollars. Null stays null: an absent fee is not a fee of zero (P-05). */
export const toDollars = (cents) => (cents == null ? null : Math.round(cents / 100))

/**
 * Nights between two dates, or null when either is missing.
 *
 * Whole days apart, computed from the DATES rather than from two instants: a stay is a number of
 * nights on a calendar, and subtracting two timestamps rounds a 30-night booking to 31 whenever
 * check-in and check-out sit at different times of day — which is the defect already sitting in
 * `canonical.reservations`.
 */
export const nightsBetweenDates = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return null
  const a = new Date(`${String(checkIn).slice(0, 10)}T00:00:00Z`)
  const b = new Date(`${String(checkOut).slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null
  const n = Math.round((b - a) / 86_400_000)
  return n > 0 ? n : null
}

/**
 * Integer cents → dollars, EXACTLY (#335). For a home's setup only.
 *
 * `toDollars` rounds to the dollar, and on Property setup that hid the stored figure: a rent saved
 * as 345050 cents showed as $3,451 and was priced at $3,451, and the Suites minimum stored as
 * 215460 showed as 2155 (#331). The page takes whole dollars only now (#334–#336), so a figure
 * with cents can only be one written before that or by something else — and it must show as what
 * it is, so the box it sits in says it is wrong instead of quietly repricing it.
 */
const dollarsExact = (cents) => (cents == null ? null : cents / 100)

/** Whole dollars → integer cents. */
export const toCents = (dollars) =>
  (dollars == null || dollars === '' ? null : Math.round(Number(dollars) * 100))

/**
 * A row of `mtr.staff` → a person, in the words the screens use.
 *
 * Added when the team screen did: until then `loadWorld` handed the raw rows straight out and
 * nothing read them except two lookup maps, so the column names had leaked exactly nowhere. A
 * screen that reads `display_name` is a screen that has to be found and changed the day that
 * column is renamed, which is the whole reason this file exists.
 */
export const staffFromRow = (row) => ({
  id: row.id,
  name: row.display_name,
  email: row.email ?? null,
  role: row.role,
  active: !!row.active,
})

/* ── homes ────────────────────────────────────────────────────────────────────────────────── */

/**
 * A row of `mtr.v_property` → the unit every screen in this product already reads.
 *
 * The shape is the one `portfolio.json` produced, field for field, so the Quote Builder, the
 * calendar, the settings screen and the automation filters need no change at all. What is new is
 * where it comes from: Guesty → raw → canonical → this view, refreshed every morning, rather than
 * a file somebody exported on the 4th of September and never again.
 *
 * `derivedMonthly` is computed here for the same reason it was computed in the snapshot: Guesty
 * holds no monthly rent, only a nightly price and a factor it multiplies by. The number is a SEED
 * for a rent somebody sets, and it is labelled derived everywhere it is shown.
 */
export function unitFromRow(row) {
  const c = row.content ?? {}
  const p = c.vendorPrices ?? {}
  const basePrice = p.basePrice ?? null
  const factor = p.monthlyPriceFactor ?? null
  return {
    id: row.id,
    /* Which business this home belongs to. Stayoo Homes is Guesty; Stayoo Suites is Cloudbeds.
       The calendar groups by it, because the two are let differently and read differently. */
    brand: row.brand ?? null,
    guestyId: row.source_ids?.guesty ?? null,
    name: row.title ?? row.nickname ?? null,
    nickname: row.nickname ?? row.title ?? null,
    street: row.address_line1 ?? null,
    city: row.city ?? null,
    state: row.region ?? null,
    zip: row.postal_code ?? null,
    lat: row.latitude ?? null,
    lng: row.longitude ?? null,
    bedrooms: row.bedrooms ?? null,
    bathrooms: row.bathrooms ?? null,
    accommodates: row.accommodates ?? null,
    /* The unit number from the vendor's address (0120) — what a lease prints beside the street. */
    unitNumber: row.address_line2 ?? null,
    propertyType: row.property_type ?? null,
    timezone: row.timezone ?? null,
    /*
      THE UNITS THAT ARE THE SAME APARTMENT (#235).

      Null for every Stayoo Home, and that is the truth rather than a gap: a house stands on its
      own. Set on a Stayoo Suites unit, it names the room type the unit is one of four copies of,
      and `availability/pool.js` is what reads it. NEVER read null as a pool of one — the two
      brands are let differently and the difference has to survive this mapper.
    */
    roomTypeId: row.room_type_id ?? null,
    /* Guesty's own tags on the listing (MTR #353), shown read-only beside ours. Never null. */
    vendorTags: Array.isArray(row.vendor_tags) ? row.vendor_tags.filter((t) => typeof t === 'string') : [],
    roomTypeName: row.room_type_name ?? null,
    active: row.status === 'active',
    amenities: c.amenities ?? [],
    /*
      THE FOUR PARAGRAPHS A RENTER ACTUALLY READS, WHICH THIS MAPPER USED TO DROP.

      Guesty holds five bodies of text per listing and this took one of them. `summary` alone
      answers "what is this place"; it does not answer "what is in it", "what is the area like",
      "how do I get about", or "what will annoy me" — which are the questions somebody deciding
      whether to live somewhere for three months is actually asking.

      The words are already in `canonical.properties` for 186 of 205 homes, refreshed every
      morning. Nothing had ever read them.
    */
    space: c.space ?? null,
    neighborhood: c.neighborhood ?? null,
    transit: c.transit ?? null,
    houseRules: c.notes ?? null,
    /** What she may use — a different question from what is in it, on a shared house. */
    access: c.access ?? null,
    /*
      NO `parking` HERE ANY MORE. Guesty's `parkingInstructions` is written for somebody who has
      already booked — in production it held a live garage keypad code on one home and internal
      links on fourteen — so canonical stopped carrying it on 2026-09-16. What a renter is told
      about parking comes from the amenity list, which Guesty publishes, or from a line on the
      home in Property setup, which is ours.
    */
    /** Stated, so an absence is a home she did not choose rather than a complaint in week two. */
    amenitiesNotIncluded: c.amenitiesNotIncluded ?? [],
    roomType: c.roomType ?? null,
    /** Room by room. "7 beds" is a number nobody can picture. */
    rooms: c.rooms ?? [],
    licenseNumber: c.licenseNumber ?? null,
    minimumAge: c.minimumAge ?? null,
    summary: c.summary ?? null,
    photoCount: c.photoCount ?? 0,
    /*
      WHETHER THIS ROW CAN BUILD WHAT A RENTER READS (#402).

      `mtr.v_property_brief` is the staff app's read: it leaves out the photographs and the five
      bodies of text, which are 3.1 MB of the 3.6 MB and which only `renterListingOf` ever uses.
      A row from it says so, and `renterListingOf` refuses it.

      SAID, NOT INFERRED. "The photographs are empty, so it must be the trimmed row" would make a
      home that genuinely has none indistinguishable from one whose photographs were never
      fetched — and on the other side of that distinction is a quote frozen with an empty listing
      in a renter's hands, with nothing on the staff side showing it.
    */
    listingTrimmed: c.listingTrimmed === true,
    /* URLs, not bytes. The snapshot inlined 2.8 MB of base64 to get pictures onto a screen. */
    photos: (c.photos ?? []).map((x) => x.url).filter(Boolean),
    photoCaptions: (c.photos ?? []).map((x) => x.caption ?? null),
    coverPhoto: c.coverPhoto ?? (c.photos ?? [])[0]?.url ?? null,
    areaSquareFeet: c.areaSquareFeet ?? null,
    beds: c.beds ?? null,
    checkInTime: c.checkInTime ?? null,
    checkOutTime: c.checkOutTime ?? null,
    prices: {
      basePrice,
      monthlyPriceFactor: factor,
      derivedMonthly:
        typeof basePrice === 'number' && typeof factor === 'number'
          ? Math.round(basePrice * 30 * factor) : null,
      cleaningFee: p.cleaningFee ?? null,
      securityDepositFee: p.securityDepositFee ?? null,
      petFee: p.petFee ?? null,
      extraPersonFee: p.extraPersonFee ?? null,
      guestsIncluded: p.guestsIncluded ?? null,
      currency: p.currency ?? 'USD',
    },
    minNights: c.minNights ?? null,
    maxNights: c.maxNights ?? null,
  }
}

/**
 * A row of `mtr.v_availability` → one day record on the calendar grid.
 *
 * `blocks` arrives as Guesty's own flag map and is passed through unchanged, because the grid does
 * not ask "is this blocked" — it asks WHY. `o` is the owner in their own home and is a
 * conversation; `bw` is a booking window that has not opened and is not a decision about this
 * date; anything else is ours to move. A boolean would paint all three the same grey.
 *
 * `cta` / `ctd` keep their nulls. Guesty leaves `ctd` off some days, absence behaves like false
 * here, and that coincidence is exactly why it must not be flattened — the same shortcut applied
 * to `status` would be a defect.
 */
export const dayFromRow = (row) => ({
  listingId: row.property_id,
  date: row.day,
  status: row.status ?? null,
  minNights: row.min_nights ?? null,
  maxNights: row.max_nights ?? null,
  allotment: row.allotment ?? undefined,
  cta: row.cta,
  ctd: row.ctd,
  /*
    ONLY THE FLAGS THAT ARE ACTUALLY SET.

    Guesty sends the whole vocabulary on every night — eleven keys, most of them false — and the
    frozen snapshot had quietly kept only the true ones. Passing the raw map through looked
    harmless and was not: `blockReasons` lists `Object.keys(blocks)`, so every free night would
    have been labelled with eleven reasons it was blocked, and the "booking window has not opened
    yet" state, which asks whether `bw` is the ONLY flag, could never be true again.

    Found by loading a real night rather than by reading the code.
  */
  blocks: Object.fromEntries(
    Object.entries(row.blocks ?? {}).filter(([, v]) => v === true)),

  /*
    WHICH PMS THIS NIGHT CAME FROM, AND THE TWO THINGS ONLY ONE OF THEM SAYS.

    Stayoo Homes come from Guesty and Stayoo Suites from Cloudbeds, and the calendar should not
    care — a night is a night. Two exceptions earn their place:

    `source` because when a night is wrong the first question is always "wrong where", and a
    screen that cannot answer it sends somebody to open two systems.

    `blockReason` and `rates` because Cloudbeds says things Guesty does not. Guesty names its
    blocks with flags (`o` is the owner, `bw` is a booking window); Cloudbeds gives one block and
    a sentence — "Block for mid term rental only" — and that sentence is more useful to the person
    reading the calendar than any flag would be. And Cloudbeds prices the same night four ways,
    one of them "Special Extended Monthly Rent" at roughly half the nightly rate: a mid-term quote
    built on the nightly number quotes a price this business does not charge.
  */
  source: row.source ?? 'guesty',
  blockReason: row.block_reason ?? null,
  rateCents: row.rate_cents ?? null,
  rates: Array.isArray(row.rate_plans) ? row.rate_plans : [],
})

/**
 * Runs → the day records every screen already reads (#377).
 *
 * `mtr.availability_runs` sends one row per STRETCH of identical nights instead of one row per
 * night: 2,334 rows instead of 13,140 for the calendar's own ninety days, 10,501 instead of
 * 105,298 for the whole horizon, 4.5 MB instead of 47. Nothing downstream of here changed, and
 * that is the point of expanding it in this file.
 *
 * IT CALLS `dayFromRow` ONCE PER NIGHT, DELIBERATELY. A loop that built its own object would be
 * a second opinion about what a day record is, and the next field added above would be missing
 * here — silently, on a screen, months later. There is one definition and this borrows it.
 *
 * A RUN IS NEVER WIDENED. `day_from` and `day_to` are used exactly as the database gave them.
 * A night the database does not hold is in no run and stays absent, which is what lets the mirror
 * answer "we do not know" instead of guessing — the distinction that failed on 2026-09-03 and
 * reported "0 units available for 90 nights from 1 November" when the calendar simply stopped
 * early. Migration 0057 and probe 0057 hold that argument in full, and the probe fails if a run
 * is ever allowed to span a hole.
 *
 * A RUN WITH NO NIGHTS IN IT THROWS RATHER THAN BEING SKIPPED. 105,298 nights arriving as 105,297
 * is exactly the kind of loss nobody notices for a month, and a run whose dates are nonsense means
 * the function is wrong — which is worth a crash with the row in the message, not a shrug.
 */
export const daysFromRuns = (runs = []) => {
  const out = []
  for (const run of runs) {
    const first = Date.parse(`${run.day_from}T00:00:00Z`)
    const last = Date.parse(`${run.day_to}T00:00:00Z`)
    if (!Number.isFinite(first) || !Number.isFinite(last) || last < first) {
      throw new Error('a run of nights that are not nights: '
        + `${run.property_id} ${run.source} ${run.day_from} → ${run.day_to}`)
    }
    for (let t = first; t <= last; t += 86_400_000) {
      out.push(dayFromRow({ ...run, day: new Date(t).toISOString().slice(0, 10) }))
    }
  }
  return out
}

/**
 * A row of `mtr.v_reservation` → a stay that came from the property management system.
 *
 * THIS IS STRICTLY BETTER THAN WHAT IT REPLACES, and it is worth saying why. Until now a stay
 * Guesty knew about was inferred from a RUN OF BOOKED NIGHTS on the calendar export, and that
 * export carries no guest, no channel and no money — so the Reservations screen showed a home and
 * two dates and had to leave the rest blank rather than invent it. Canonical carries all three.
 */
/*
  THE COLUMNS THIS MAPPER READS, AND NOTHING ELSE (MTR #657).

  `select('*')` on this view is seventeen columns and 3.9 MB for the 8,196 rows the staff app reads
  — measured on production 2026-10-21 — and it is read before any screen is drawn, so every
  colleague waits for all of it. Six of those columns (`nights`, `currency`, `distribution_path`,
  `booked_at`, `canceled_at`, `source_last_updated_at`) are read by nothing: `nights` is worked out
  from the two dates in `buildReservations`, and the other five appear nowhere in this product. The
  same query asking for the eleven below is 2,732 kB and 533 ms instead of 3,908 kB and 806 ms.

  IT LIVES BESIDE THE MAPPER ON PURPOSE. A column list in the store and a mapper here are two
  statements about the same thing, and the way that breaks is silent: a mapper reaching for a
  column nobody asked for reads `undefined`, and a stay loses its guest's name or its money with
  nothing failing. So the list is here, and `eachReservationColumnIsRead` holds the two together —
  it reads this function's own source and fails if it touches a column the list does not name.
*/
export const RESERVATION_COLUMNS = 'id,property_id,confirmation_code,status,check_in,check_out,'
  + 'guests_count,money_total,guest_name,channel,source_ids'

export const vendorReservationFromRow = (row, unitById) => ({
  id: row.id,
  confirmationCode: row.confirmation_code ?? null,
  guestyId: row.source_ids?.guesty ?? null,
  unit: unitById.get(row.property_id) ?? { id: row.property_id, nickname: null },
  checkIn: row.check_in,
  checkOut: row.check_out,
  status: row.status,
  renterName: row.guest_name ?? null,
  channel: row.channel ?? null,
  agreedCents: row.money_total == null ? null : Math.round(Number(row.money_total) * 100),
  occupants: row.guests_count ?? null,
})

/* ── leads, and everything hanging off one ────────────────────────────────────────────────── */

/*
  A STAGE THAT HAS BEEN RENAMED ARRIVES UNDER ITS NEW NAME. (#554 step B; the list is empty today.)

  This product's migrations are applied to production BY HAND and the code deploys on merge, so
  there is no instant at which both halves of a rename change together. Move the rows first and
  the running OLD code meets a word it has no label for; deploy first and the NEW code meets the
  old word, same thing — a column of leads with a blank stage, and the Reports conversion rate at
  nothing while somebody is looking at it.

  This is what makes the order stop mattering: while a word is being retired the database accepts
  both, nothing writes the old one, and every row that still carries it arrives here as the new
  one. WON → CONFIRMED used it from 2026-10-15 until 0098 moved the last 27 rows, about four
  hours later.

  THE MECHANISM STAYS AND THE LIST EMPTIES. `RETIRING_STAGES` is the debt; this function is how it
  is paid. Deleting the function would mean rebuilding it from memory the next time a stage is
  renamed, which is how the first version of it came to be a hardcoded `stage === 'WON'`.
*/
const sameStage = (stage) => RETIRING_STAGES[stage] ?? stage

/*
  WHAT THE DATABASE SAID WHEN THIS COPY OF THE LEAD WAS READ (2026-10-19).

  Two tabs on one lead: one moved it to Contract Pending at 11:50; the other, open since 11:11, sent
  a quote at 13:42 and wrote its whole 11:11 lead back — Not won again, twice in one afternoon. A
  save must write what THIS copy changed, nothing else. So each lead carries the row it was read
  as, under a symbol: \`{ ...lead, stage }\` keeps it (spread copies symbol keys), JSON drops it, and
  \`saveLeadNow\` sends only the columns that differ from it.
*/
export const LOADED_AS = Symbol('loadedAs')

export const leadFromRow = (row, opts) => {
  const lead = leadFromRowOnly(row, opts)
  Object.defineProperty(lead, LOADED_AS, { value: leadToRow(lead), enumerable: true, writable: false, configurable: true })
  return lead
}

const leadFromRowOnly = (row, { events = [], quotes = [] } = {}) => ({
  id: row.id,
  reference: row.reference,
  name: row.name,
  email: row.email,
  emailIsRelay: row.email_is_relay,
  phone: row.phone,
  /*
    THE NUMBER EXACTLY AS IT ARRIVED (0037, mtr#169).

    `phone` is E.164 or null, because the database will not hold anything else. When it is null
    and THIS is not, somebody sent us a number nobody can read — thirteen digits with no country
    code, an extension, a sentence. It is on the screen rather than thrown away, because throwing
    it away loses the only way to reach her and guessing at it texts a stranger.
  */
  phoneAsGiven: row.phone_as_given ?? null,
  source: row.source,
  /*
    THE OTA RESERVATION THIS CONVERSATION IS ATTACHED TO, by Guesty's own id.

    An OTA lead has no budget and never will — the guest did not ask what it costs, Airbnb told
    her. What she has is a PRICE, and it is in `canonical.reservations`. This is the only reliable
    way to reach it: four Airbnb enquiries in this account share the same dates on four different
    homes at three different prices, so a match on dates would be wrong three times in four and
    look exactly as confident as if it were right.
  */
  reservationRef: row.reservation_ref ?? null,
  stage: sameStage(row.stage),
  stageEnteredAt: row.stage_entered_at,
  notWonReason: row.not_won_reason ?? null,
  checkIn: row.check_in,
  checkOut: row.check_out,
  nights: row.nights,
  guests: row.guests,
  budgetMonthly: toDollars(row.budget_monthly_cents),
  /*
    THE REST OF THE BRIEF THE RENTER GAVE US.

    Every one of these was dropped on the way in until 2026-09-16: the form said `city`, the
    mapper said `area`, and nothing objected. `quoteBriefFor` reads them to carry a lead into the
    quote builder, so losing them meant an agent re-typing what the renter had already said —
    the exact retyping the quote builder was written to remove.

    A single budget figure is a CEILING, so the ceiling column answers `budgetMax` as well.
  */
  budgetMax: toDollars(row.budget_monthly_cents),
  budgetMin: toDollars(row.budget_min_cents),
  bedrooms: row.bedrooms ?? null,
  pets: row.pets ?? null,
  facility: row.facility ?? null,
  /*
    THE THREE THE WEBSITE FORM ASKS AND NOTHING ELSE DOES (#44, migration 0018).

    Its fourth — what the renter typed into the notes box — is NOT here. Those words are an
    inbound message on the timeline, the same place a Furnished Finder enquiry keeps them, and
    a column would be the second place this product already decided not to have.

    `durationBand` is a band and stays one — "1-3 months" is not a number of nights, and the
    moment it becomes one somebody is quoting against a check-out date the renter never gave.
    `datesFlexible` is tri-state: null is "nobody asked", which is not "no".
  */
  stayPurpose: row.stay_purpose ?? null,
  durationBand: row.duration_band ?? null,
  datesFlexible: row.dates_flexible ?? null,
  propertyWanted: row.property_wanted ?? null,
  /* What somebody said THIS conversation is about, overruling what the spelling means (0092). */
  wantedBuilding: row.wanted_building ?? null,
  unitType: row.unit_type,
  area: row.area,
  nextStep: row.next_step,
  currentQuoteId: row.current_quote_id,
  /*
    The channel's own thread, for an enquiry that arrived with no way to reach the person.

    It is READ and never written back: `leadToRow` has no `reply_url`, because this is the
    channel's fact about its own conversation and nothing in this product is entitled to an
    opinion about it. The Inbox uses it to offer "Reply on Furnished Finder" in place of a text
    box that would do nothing.
  */
  replyUrl: row.reply_url ?? null,
  /*
    THE CHANNEL'S OWN ID FOR THIS CONVERSATION, and for an OTA enquiry it is the only way to
    answer the person. Airbnb and VRBO hand over a relay address and keep the renter; the place
    they are actually reading is the thread on the site they booked through. Read and never
    written, for the same reason as `replyUrl`: it is the channel's fact, not ours.
  */
  threadKey: row.thread_key ?? null,
  /* When the enquiry reached us — what the leads page filters a time window against. */
  createdAt: row.created_at ?? null,
  /*
    Set when this lead turned out to be a duplicate of another and was folded into it — Furnished
    Finder mails twice about one renter, once as a broadcast with no conversation id and once as a
    message with one, and for a while that opened two leads for one person. The row is kept, so
    the merge can be checked afterwards; it is simply no longer a lead anybody works.

    It is mapped here rather than read as `merged_into` wherever it is needed, because the column
    name is this file's business and nothing else's. `loadWorld` in store.js is the only caller
    that acts on it today, and it acts by not returning the lead at all.
  */
  mergedInto: row.merged_into ?? null,
  timeline: events,
  quotes,
})

/*
  `reference` IS DELIBERATELY ABSENT WHEN WE DO NOT HAVE ONE.

  `mtr.leads.reference` defaults to `'MTR-' || nextval(mtr.lead_reference_seq)`. A default only
  fires when the column is not named in the insert, so sending `reference: lead.id` — a uuid — or
  a number the browser counted out would override the one control that makes these unique. It did:
  on 2026-09-16 a hand-typed lead was refused with `duplicate key value violates unique constraint
  "leads_reference_key"` because the browser had counted 21 leads and the table held 23.
*/
export const leadToRow = (lead) => ({
  ...(lead.reference ? { reference: lead.reference } : {}),
  name: lead.name,
  email: lead.email ?? null,
  email_is_relay: !!lead.emailIsRelay,
  /*
    WRITE THE RAW BACK WHEN THERE IS NOTHING ELSE TO WRITE (0037, mtr#169).

    0037's trigger reads this column and derives both: what it can dial goes in `phone`, what
    arrived goes in `phone_as_given`, and clearing it clears both. So a lead whose number we could
    not read has `phone: null`, and saving it for an unrelated reason — a stage change, a note —
    would send `null` and wipe the evidence. Sending the raw back instead makes every save
    re-derive exactly what is already there.
  */
  phone: lead.phone ?? lead.phoneAsGiven ?? null,
  source: lead.source ?? null,
  stage: lead.stage,
  stage_entered_at: lead.stageEnteredAt,
  not_won_reason: lead.notWonReason ?? null,
  check_in: lead.checkIn ?? null,
  check_out: lead.checkOut ?? null,
  /*
    NIGHTS IS DERIVED, NEVER ASKED FOR. Both dates are on the form; making somebody type the
    number between them is how one lead came to say "45 nights" on the quote screen and
    "Length — not given" on the panel beside it.
  */
  nights: lead.nights ?? nightsBetweenDates(lead.checkIn, lead.checkOut),
  guests: lead.guests ?? lead.occupants ?? null,
  /* A single figure is the ceiling; `budgetMonthly` is what a channel lead calls the same thing. */
  budget_monthly_cents: toCents(lead.budgetMax ?? lead.budgetMonthly),
  budget_min_cents: toCents(lead.budgetMin),
  unit_type: lead.unitType ?? null,
  area: lead.area ?? lead.city ?? null,
  bedrooms: lead.bedrooms ?? null,
  pets: lead.pets ?? null,
  facility: lead.facility ?? null,
  property_wanted: lead.propertyWanted ?? lead.property ?? null,
  wanted_building: lead.wantedBuilding ?? null,
  stay_purpose: lead.stayPurpose ?? null,
  duration_band: lead.durationBand ?? null,
  /* `?? null` and not `!!`: false is "they said no", null is "nobody asked". */
  dates_flexible: lead.datesFlexible ?? null,
  next_step: lead.nextStep ?? null,
})

/**
 * One line of a lead's history.
 *
 * The timeline is the only place in this product that is append-only by intent: nothing edits a
 * past entry, because a history that can be edited is not evidence of anything. Whatever a kind
 * needs beyond the common fields rides in `meta` rather than earning a column — a stage change
 * needs `from`/`to`, a quote entry needs a quote id, and inventing a column for each would leave
 * most of them null on most rows.
 */
/*
  ONE ACTOR COLUMN, TWO NAMES IN MEMORY — AND THE WAY BACK WAS MISSING. (Copilot, mtr#84.)

  `mtr.lead_events` has a single `author` column. In the browser the same fact goes by two names:
  a message calls it `author`, and everything a person did — a note, a stage change, a price lock
  — calls it `by`. `eventToRow` already folds them together (`author ?? by`), so writing is fine.
  Reading was not: this mapper restored `author` only, while `Timeline` prints `e.by`. So the line
  saying who closed a lead was correct until the page was reloaded and then said nobody.

  It is restored under both names off the one column, which cannot disagree with itself. A `by`
  left in `meta` by a row written before `eventToRow` learned to fold it is the fallback, so old
  rows keep their person too.

  EXCEPT ON AN INBOUND MESSAGE, WHERE `author` IS NOT AN ACTOR. (Copilot, mtr#84, second round.)
  On a message from the renter, `author` holds HER display name — she is the sender, not somebody
  who did something to the lead. `Timeline` prints `by` in the line that means "who did this", so
  copying it across would turn `Zillow · 14:02` into `Zillow · Dana Kestrel · 14:02` and file the
  renter as an actor on her own enquiry. Her name is already the entry's title. Only a legacy
  `meta.by` applies there.
*/
const inbound = (row) => row.kind === 'message' && row.direction === 'in'

/*
  THE COLUMNS WIN OVER THE META BLOB. (MTR #132, 2026-09-28.)

  The worker that reads Zillow and Furnished Finder mail (SAOS `inbound-mail`, since 2026-09-20)
  writes each email as `kind = 'message'` and keeps what its parser read in `meta` — including
  `meta.kind`, the PARSER's word for the email: `housing_request`, `booking_inquiry`. This mapper
  spread `meta` after the columns, so that word overwrote the row's `kind`. Every inbound email
  from those two channels then reached the browser as a kind nothing knows: `Timeline` titled it
  "Note" and drew no text, and the Inbox, which keeps only `message`, left it out of the
  conversation. The Product Owner opened MTR-2171 and saw "no content at all" — the text was in
  the database the whole time.

  `eventToRow` already strips every column name out of `meta`, so nothing this product writes can
  collide; only a writer outside it can. So the columns are applied last, and the parser's word is
  kept under a name of its own rather than thrown away.
*/
/*
  WHAT GUESTY SAYS HAPPENED TO A MESSAGE WE POSTED ON A CHANNEL (FB-0009, MTR #293). The Guesty
  poller keeps Guesty's own post status as `meta.delivery_status` (SAOS connector-guesty,
  inboxPoll.ts). On 2026-10-01, 11 of 51 such messages were `failed` — Airbnb never delivered them —
  and every one showed here as sent, because only our own `delivery` was ever read. A message the
  channel did not deliver is a failed send, said on the message, with the one reason we know.
*/
const CHANNEL_FAILED = 'Guesty reports that the channel did not deliver it. Check it in Guesty and send it again.'
/* Our own SENT only ever meant Guesty took it; Guesty saying the channel did not deliver it is later
   and better evidence. A person's own word (sent by hand) and our own failures stand (review, #294). */
const channelDelivery = (meta, direction) => (direction === 'out' && (meta.delivery == null || meta.delivery === 'SENT')
  && String(meta.delivery_status ?? '').toLowerCase() === 'failed'
  ? { delivery: 'FAILED', failure: meta.failure ?? CHANNEL_FAILED } : {})

export const eventFromRow = (row) => {
  const { kind: inquiryKind, ...meta } = row.meta ?? {}
  return {
    ...meta,
    ...channelDelivery(meta, row.direction),
    ...(inquiryKind != null && inquiryKind !== row.kind ? { inquiryKind } : {}),
    id: row.id,
    kind: row.kind,
    at: row.at,
    body: row.body,
    channel: row.channel,
    direction: row.direction,
    author: row.author,
    by: inbound(row) ? (row.meta?.by ?? null) : (row.author ?? row.meta?.by ?? null),
    /* When the database wrote it, as against `at`, when it was said — a conversation imported with its
       past is told apart from somebody writing now by the gap (FB-0019, review of #732). Read only. */
    ...(row.created_at ? { createdAt: row.created_at } : {}),
    /* A stage_change written before the rename says WON; the timeline reads it as Confirmed. */
    ...(row.kind === 'stage_change'
      ? { from: sameStage(meta.from), to: sameStage(meta.to) }
      : {}),
  }
}

/**
 * The kinds a timeline entry may actually be.
 *
 * THIS LIST IS WRITTEN FROM WHAT THE PRODUCT PRODUCES, NOT FROM WHAT SOUNDED RIGHT.
 *
 * The first version of it was a guess, and two of the guesses were wrong in the same direction:
 * the product writes `quote_sent` (leadQuote.js) and `hold_ended` (App.jsx), neither of which was
 * on the list, so both were silently rewritten to `system` on the way into the database. Nothing
 * failed. The entry saved, the screen kept rendering the object still in memory, and only after a
 * reload did `Timeline` — which keys its icon, its title and its whole body off `kind` — fall back
 * to a blank "Note". The quote detail and the hold's outcome were gone, and the timeline is the
 * one part of this product that is meant to be evidence.
 *
 * A silent coercion is the wrong shape for this. It is kept only as the last resort for a kind
 * nobody has taught this file yet, because losing a timeline entry entirely is worse than losing
 * its label — but the list is now the product's own vocabulary, kept in step with the Inbox conversation's `activityWords` (the lead page's `Timeline.jsx` was retired, MTR #161)
 * and with the CHECK constraint on `mtr.lead_events.kind` (widened in migrations 0005 and 0022).
 */
export const EVENT_KINDS = [
  'message',      // inbox/intake.js, inbox/outbound.js
  'stage_change', // leads/stages.js
  'quote_sent',   // leads/leadQuote.js
  'hold',         // leads/App.jsx — a home taken off the market
  'hold_ended',   // leads/App.jsx — released, converted or run out
  'price_locked', // leads/App.jsx, migration 0022 — Confirm locked her price (MTR #48)
  'home_declined', // leads/homesToQuote.js, migration 0118 — she does not want this home, and why
  'note',
  'quote',
  'deal',
  'task',
  'system',
]

export const eventToRow = (leadId, e) => {
  /* `createdAt` is the database's own column, never written back into `meta`. */
  const { kind, at, body, channel, direction, author, by, id, createdAt, ...meta } = e
  return {
    lead_id: leadId,
    at,
    kind: EVENT_KINDS.includes(kind) ? kind : 'system',
    body: body ?? null,
    channel: channel ?? null,
    direction: direction ?? null,
    author: author ?? by ?? null,
    meta,
  }
}

/**
 * A frozen quote, reassembled.
 *
 * `frozen` holds the priced payload exactly as it went to the renter, and it is the source of
 * truth for anything the renter saw. The columns beside it exist so the database can be asked
 * questions — which quotes expire today, which options need an owner's yes — without opening a
 * jsonb blob for every row. Where the two could disagree the columns are derived FROM the payload
 * at write time, never typed separately.
 */
export const quoteFromRow = (row, options = []) => ({
  ...(row.frozen ?? {}),
  id: row.id,
  /*
    THE NAME A PERSON SAYS, WHICH THIS MAPPER USED TO DESTROY.

    `id` has to be the uuid: `leads.current_quote_id` is a uuid foreign key and every "which
    quote is this" comparison in the product goes through it. But the frozen payload's own id is
    `Q-2125-4` — the thing somebody says down a phone — and spreading the payload and then
    overwriting `id` threw it away. The Inbox showed a renter's quote as
    "8c1c516b-4c25-4141-acf1-662797db3fe0".

    So the uuid stays the KEY and the reference becomes its own field. Everything that PRINTS a
    quote prints `ref`; everything that MATCHES one still matches `id`.
  */
  ref: row.ref ?? row.frozen?.id ?? null,
  leadId: row.lead_id,
  version: row.version,
  supersedes: row.supersedes_id,
  sentAt: row.sent_at,
  sentBy: row.sent_by,
  expiresAt: row.expires_at,
  basis: row.basis,
  cleaning: row.cleaning,
  stay: { checkIn: row.stay_check_in, checkOut: row.stay_check_out },
  options: mergeQuoteOptions(row.frozen?.options ?? [], options),
})

/**
 * The frozen option and its row, put back together — frozen first.
 *
 * WHY MERGING, AND NOT SIMPLY SWAPPING WHICH ONE WINS
 *
 * This preferred the option ROWS whenever any existed, and the rows are a projection: they carry
 * the dozen columns the database needs to be asked questions with. `freezeQuote` stores far more
 * — `street`, `bathrooms`, `accommodates`, `photo`, `amenities`, and the entire `cleaning` object
 * holding the menu the renter chooses from. None of that has a column, so a reopened quote came
 * back without it and `RenterQuote` printed "undefined bath · sleeps undefined" on the one page in
 * this product with an audience outside the company.
 *
 * Preferring `frozen` outright would have fixed the printing and lost the thing the rows are FOR:
 * a row has a uuid, and a hold points at exactly one of them (`holds.option_id`). Drop the row and
 * that foreign key has nothing on this side to land on.
 *
 * So neither wins as a whole. The frozen snapshot is the BASE — it is the record of what was sent,
 * and a frozen quote is a historical document: what went out must come back as it went out. The
 * row is layered over it for the fields where the DATABASE is the authority: its own `id`, and the
 * canonical `property_id`. Every other column was derived FROM this same payload at write time
 * (see `quoteOptionToRow`), so the two agree by construction and layering them changes nothing.
 *
 * Only VALUES the row actually has are layered. A null column means "this was never stored",
 * never "the renter was sent nothing there" — and an empty `fees` or `segments` array is the same
 * statement in a different shape. Either one written over the frozen payload would delete evidence.
 */
const mergeQuoteOptions = (frozen = [], rows = []) => {
  if (rows.length === 0) return frozen
  const byUnit = new Map(frozen.filter((o) => o.unitId != null).map((o) => [o.unitId, o]))
  const used = new Set()
  const merged = rows.map((r, i) => {
    const base = (r.unitId != null ? byUnit.get(r.unitId) : null) ?? frozen[i] ?? {}
    used.add(base)
    return { ...base, ...stored(r) }
  })
  /* An option that was sent but has no row is still an option that was sent. It is kept rather
     than dropped, because the renter's copy is the record and this side may only add to it. */
  return [...merged, ...frozen.filter((o) => !used.has(o))]
}

/** The fields a row actually carries — nulls and empty lists are absence, not content. */
const stored = (o) => Object.fromEntries(
  Object.entries(o).filter(([, v]) => v != null && !(Array.isArray(v) && v.length === 0)))

export const quoteOptionFromRow = (row) => ({
  id: row.id,
  unitId: row.property_id,
  name: row.name,
  city: row.city,
  bedrooms: row.bedrooms,
  rentCents: row.rent_cents,
  depositCents: row.deposit_cents,
  totalCents: row.total_cents,
  impliedNightlyCents: row.implied_nightly_cents,
  cleaningDescription: row.cleaning_description,
  needsOwnerConfirmation: row.needs_owner_confirmation,
  fees: row.fees ?? [],
  segments: row.segments ?? [],
})

export const quoteToRow = (leadId, q) => ({
  lead_id: leadId,
  version: q.version ?? 1,
  supersedes_id: q.supersedes ?? null,
  sent_at: q.sentAt ?? null,
  sent_by: q.sentBy ?? null,
  expires_at: q.expiresAt ?? null,
  /*
    HOW AND WHERE IT WENT — the three columns that are the whole point of the send dialog.

    "The stage moves on the record of a send, not on the button," it says, and then asks which
    channel and which address. Those answers live on the send evidence, not on the quote record,
    so reading them off the quote alone left all three null on every quote ever sent: the product
    asked the question, the person answered it, and nothing kept the answer.
  */
  send_channel: q.send?.channel ?? q.sendChannel ?? null,
  sent_to_email: q.send?.destinations?.email ?? q.sentToEmail ?? null,
  sent_to_phone: q.send?.destinations?.phone ?? q.sentToPhone ?? null,
  basis: q.basis ?? null,
  cleaning: q.cleaning ?? null,
  stay_check_in: q.stay?.checkIn ?? null,
  stay_check_out: q.stay?.checkOut ?? null,
  /* The record's own id IS the spoken reference — `Q-2124-1` — so it is the fallback here. This
     column is the quote's identity in the database; the uuid primary key is bookkeeping. */
  ref: q.ref ?? q.id ?? null,
  frozen: q,
})

export const quoteOptionToRow = (quoteId, o, position = 0) => ({
  quote_id: quoteId,
  property_id: o.unitId,
  name: o.name ?? null,
  city: o.city ?? null,
  bedrooms: o.bedrooms ?? null,
  rent_cents: o.rentCents ?? null,
  deposit_cents: o.depositCents ?? null,
  total_cents: o.totalCents ?? null,
  implied_nightly_cents: o.impliedNightlyCents ?? null,
  cleaning_description: o.cleaningDescription ?? null,
  needs_owner_confirmation: !!o.needsOwnerConfirmation,
  fees: o.fees ?? [],
  segments: o.segments ?? [],
  position,
})

/* ── holds and deals ──────────────────────────────────────────────────────────────────────── */

/**
 * `expires_at` comes back exactly as it was written and is never recomputed.
 *
 * That is the promise `createHold` makes: if the policy becomes 48 hours tomorrow, a hold made
 * today keeps the deadline a person was actually given. A round trip through the database is
 * precisely where a recomputation would sneak in, so it does not happen here either.
 */
export const holdFromRow = (row) => ({
  id: row.id,
  leadId: row.lead_id,
  quoteId: row.quote_id,
  quoteVersion: row.quote_version,
  optionId: row.option_id,
  unitId: row.property_id,
  unitName: row.property_name,
  checkIn: row.check_in,
  checkOut: row.check_out,
  totalCents: row.total_cents,
  at: row.placed_at,
  by: row.placed_by,
  hours: row.hours,
  expiresAt: row.expires_at,
  releasedAt: row.released_at,
  releasedBy: row.released_by,
  releaseReason: row.release_reason,
  convertedAt: row.converted_at,
  reservationRef: row.reservation_ref,
})

export const holdToRow = (h) => ({
  lead_id: h.leadId,
  quote_id: h.quoteId ?? null,
  quote_version: h.quoteVersion ?? null,
  option_id: h.optionId ?? null,
  property_id: h.unitId,
  property_name: h.unitName ?? null,
  check_in: h.checkIn,
  check_out: h.checkOut,
  total_cents: h.totalCents ?? null,
  placed_at: h.at,
  placed_by: h.by,
  hours: h.hours,
  expires_at: h.expiresAt,
  released_at: h.releasedAt ?? null,
  released_by: h.releasedBy ?? null,
  release_reason: h.releaseReason ?? null,
  converted_at: h.convertedAt ?? null,
  reservation_ref: h.reservationRef ?? null,
})

/**
 * A price lock (MTR #48) — what Confirm makes now. Same frozen-deadline promise as a hold:
 * `expires_at` is read back exactly as written and never recomputed.
 */
export const priceLockFromRow = (row) => ({
  id: row.id,
  leadId: row.lead_id,
  quoteId: row.quote_id,
  quoteVersion: row.quote_version,
  optionId: row.option_id ?? null,
  unitId: row.property_id,
  unitName: row.property_name,
  checkIn: row.check_in,
  checkOut: row.check_out,
  totalCents: row.total_cents,
  cleaningId: row.cleaning_id ?? null,
  at: row.placed_at,
  by: row.placed_by,
  hours: row.hours,
  expiresAt: row.expires_at,
  releasedAt: row.released_at ?? null,
  releasedBy: row.released_by ?? null,
  releaseReason: row.release_reason ?? null,
  convertedAt: null,
  reservationRef: null,
})

export const priceLockToRow = (l) => ({
  lead_id: l.leadId,
  quote_id: l.quoteId ?? null,
  quote_version: l.quoteVersion ?? null,
  property_id: l.unitId,
  property_name: l.unitName ?? null,
  check_in: l.checkIn,
  check_out: l.checkOut,
  total_cents: l.totalCents ?? null,
  cleaning_id: l.cleaningId ?? null,
  placed_at: l.at,
  placed_by: l.by,
  hours: l.hours,
  expires_at: l.expiresAt,
  released_at: l.releasedAt ?? null,
  released_by: l.releasedBy ?? null,
  release_reason: l.releaseReason ?? null,
})

/**
 * A deal is a header and up to four independent facts, and the database keeps them that way.
 *
 * The product carries them as `steps: { PAID: { firstPaymentRef, firstPaymentCents, at, by } }`.
 * The table stores one row per step with a generic `ref`, because every step has exactly one
 * reference and four nullable reference columns would leave three empty on every row. The names
 * the product uses are restored here.
 */
const STEP_REF_NAME = {
  CONTRACT_OUT: 'contractRef',
  SIGNED: 'signatureRef',
  PAID: 'firstPaymentRef',
  RESERVED: 'reservationRef',
}

export const dealFromRows = (row, stepRows = []) => ({
  id: row.id,
  leadId: row.lead_id,
  holdId: row.hold_id,
  quoteId: row.quote_id,
  quoteVersion: row.quote_version,
  optionId: row.option_id,
  unitId: row.property_id,
  checkIn: row.check_in,
  checkOut: row.check_out,
  agreedFirstPaymentCents: row.agreed_first_payment_cents,
  openedAt: row.opened_at,
  steps: Object.fromEntries(stepRows.map((s) => [s.step, {
    [STEP_REF_NAME[s.step]]: s.ref,
    ...(s.step === 'PAID' ? { firstPaymentCents: s.amount_cents } : {}),
    at: s.at,
    by: s.by,
  }])),
})

export const dealToRow = (d) => ({
  lead_id: d.leadId,
  hold_id: d.holdId ?? null,
  quote_id: d.quoteId ?? null,
  quote_version: d.quoteVersion ?? null,
  option_id: d.optionId ?? null,
  property_id: d.unitId,
  check_in: d.checkIn,
  check_out: d.checkOut,
  agreed_first_payment_cents: d.agreedFirstPaymentCents ?? null,
  opened_at: d.openedAt,
})

export const dealStepToRow = (dealId, step, fields) => ({
  deal_id: dealId,
  step,
  ref: fields[STEP_REF_NAME[step]],
  amount_cents: step === 'PAID' ? (fields.firstPaymentCents ?? null) : null,
  at: fields.at,
  by: fields.by ?? 'agent',
})

/* ── work ─────────────────────────────────────────────────────────────────────────────────── */

export const categoryFromRow = (row) => ({ id: row.id, label: row.label, builtIn: row.built_in })
export const categoryToRow = (c, sort = 100) =>
  ({ id: c.id, label: c.label, built_in: !!c.builtIn, sort })

export const templateFromRow = (row, staffById = new Map()) => ({
  id: row.id,
  name: row.name,
  title: row.title,
  description: row.description ?? '',
  category: row.category_id,
  priority: row.priority,
  assignee: staffById.get(row.assignee_id) ?? null,
  checklist: row.checklist ?? [],
  createdAt: row.created_at,
})

export const templateToRow = (t, staffIdByName = new Map()) => ({
  name: t.name,
  title: t.title,
  description: t.description ?? '',
  category_id: t.category,
  priority: t.priority,
  assignee_id: staffIdByName.get(t.assignee) ?? null,
  checklist: t.checklist ?? [],
})

/*
  A FOLDER SOMEBODY SAVED down the left of the Inbox (migration 0087). The shape and every rule
  about it live in `src/inbox/savedFolders.js`, with the screen that draws it; this is only the door
  out to the database's columns. There is no way back in here on purpose: the rows arrive with the
  rest of the world and the Inbox reads them with `foldersFrom`, which also decides whose each is.
*/
export { folderToRow as inboxFolderToRow } from '../inbox/savedFolders.js'

/**
 * WHAT ONE SPELLING MEANS — a row of `mtr.property_wanted_aliases` as the product reads it.
 *
 * `phrase_key` is the flattened spelling and the key everything matches on; `phrase` is what was
 * actually typed and is only ever shown back to a person.
 */
export const propertyAliasFromRow = (row) => ({
  id: row.id,
  phrase: row.phrase ?? '',
  phraseKey: row.phrase_key ?? '',
  propertyId: row.property_id ?? null,
  building: row.building ?? null,
  notOurs: Boolean(row.not_ours),
  decidedBy: row.decided_by ?? 'PERSON',
  updatedAt: row.updated_at ?? null,
})

export const taskFromRow = (row, staffById = new Map()) => ({
  id: row.id,
  title: row.title,
  description: row.description ?? '',
  category: row.category_id,
  priority: row.priority,
  status: row.status,
  assignee: staffById.get(row.assignee_id) ?? null,
  unitId: row.property_id,
  reservationId: row.reservation_id,
  leadId: row.lead_id,
  dueDate: row.due_date,
  dueTime: row.due_time,
  startDate: row.start_date,
  reminder: row.reminder,
  attachments: row.attachments ?? [],
  checklist: row.checklist ?? [],
  templateId: row.template_id,
  dedupKey: row.dedup_key,
  staleReason: row.stale_reason ?? null,
  /*
    THE TABLE SAYS 'RULE', THE ENGINE AND THE QUEUE SAY 'AUTOMATION'. The column's check constraint
    allows PERSON or RULE; everything in memory calls a rule's work AUTOMATION and carries the rule
    on `source.ruleId`. Translated here and in taskToRow, the only two places that see both, so a
    task a rule made is still a rule's task after a reload. (Copilot, #40: until this, every task a
    rule made was written as PERSON with no rule id.)
  */
  source: row.source_kind === 'RULE'
    ? { kind: 'AUTOMATION', label: row.source_label, ruleId: row.rule_id ?? null }
    : { kind: row.source_kind, label: row.source_label },
  createdAt: row.created_at,
  completedAt: row.completed_at,
})

export const taskToRow = (t, staffIdByName = new Map()) => ({
  title: t.title,
  description: t.description ?? '',
  category_id: t.category,
  priority: t.priority,
  status: t.status,
  assignee_id: staffIdByName.get(t.assignee) ?? null,
  property_id: t.unitId ?? null,
  reservation_id: t.reservationId ?? null,
  lead_id: t.leadId ?? null,
  due_date: t.dueDate,
  due_time: t.dueTime ?? null,
  start_date: t.startDate ?? null,
  reminder: t.reminder ?? null,
  attachments: t.attachments ?? [],
  checklist: t.checklist ?? [],
  template_id: t.templateId ?? null,
  /*
    THE COLUMN THAT MAKES AN AUTOMATION SAFE.

    Empty string is not a key. A task typed by a person has no dedup key at all, and writing `''`
    would make the SECOND hand-typed task collide with the first — the unique index would refuse
    it, and somebody would be told the database was broken. Null does not collide with null.
  */
  dedup_key: t.dedupKey || null,
  source_kind: t.source?.kind === 'AUTOMATION' || t.source?.kind === 'RULE' ? 'RULE' : 'PERSON',
  source_label: t.source?.label ?? 'Created by hand',
  rule_id: t.source?.ruleId ?? t.ruleId ?? null,
  stale_reason: t.staleReason ?? null,
  completed_at: t.completedAt ?? null,
})

/* ── automations ──────────────────────────────────────────────────────────────────────────── */

/**
 * Filters, repeat and actions stay as they are written.
 *
 * They are a small tree the builder owns and keeps changing — a new comparison, a new field, a
 * weekday on a weekly repeat. Splitting them into tables would mean a database migration every
 * time the builder learns something, which is how a builder stops learning. The trigger is
 * promoted to columns instead, because that is the one part queried BY the engine.
 */
export const ruleFromRow = (row) => ({
  id: row.id,
  name: row.name,
  trigger: {
    event: row.trigger_event,
    direction: row.trigger_direction,
    amount: row.trigger_amount,
    unit: row.trigger_unit,
  },
  filters: upgradeFilters(row.filters ?? []),
  repeat: row.repeat ?? {},
  actions: row.actions ?? [],
  cooldownDays: row.cooldown_days ?? 0,
  active: row.active,
  activatedAt: row.activated_at,
  createdAt: row.created_at,
})

export const ruleToRow = (r) => ({
  name: r.name,
  trigger_event: r.trigger.event,
  trigger_direction: r.trigger.direction,
  trigger_amount: r.trigger.amount ?? 0,
  trigger_unit: r.trigger.unit ?? 'd',
  filters: r.filters ?? [],
  repeat: r.repeat ?? {},
  actions: r.actions ?? [],
  cooldown_days: r.cooldownDays ?? 0,
  active: !!r.active,
  activated_at: r.activatedAt ?? null,
})

export const messageFromRow = (row) => ({
  id: row.id,
  name: row.name,
  channels: row.channels ?? [],
  subject: row.subject,
  body: row.body,
  text: row.sms_text,
  createdAt: row.created_at,
})

export const messageToRow = (m) => ({
  name: m.name,
  channels: m.channels ?? ['EMAIL'],
  subject: m.subject ?? null,
  body: m.body ?? null,
  sms_text: m.text ?? null,
})

export const draftFromRow = (row) => ({
  id: row.id,
  kind: 'MESSAGE_DRAFT',
  ruleId: row.rule_id,
  dedupKey: row.dedup_key,
  to: row.recipient,
  channels: row.channels ?? [],
  subject: row.subject,
  body: row.body,
  text: row.sms_text,
  leadId: row.lead_id,
  reservationId: row.reservation_id,
  unitId: row.property_id,
  status: row.status,
  createdAt: row.created_at,
})

export const draftToRow = (d) => ({
  rule_id: d.ruleId ?? null,
  dedup_key: d.dedupKey || null,
  recipient: d.to ?? 'RENTER',
  channels: d.channels ?? [],
  subject: d.subject ?? null,
  body: d.body ?? null,
  sms_text: d.text ?? null,
  lead_id: d.leadId ?? null,
  reservation_id: d.reservationId ?? null,
  property_id: d.unitId ?? null,
  status: 'DRAFT',
})

/* ── what we have decided about a home ────────────────────────────────────────────────────── */

/**
 * Money crosses here in both directions.
 *
 * `property_settings` stores cents; `seedSettings` and every settings screen work in whole dollars
 * because that is what somebody types into a rent field, and since #334–#336 that is all they
 * accept (`validate`, src/property/settings.js). It is read back EXACTLY (`dollarsExact`): rounding
 * to the dollar on the way out used to look lossless and was not — it repriced a stored 3450.50 as
 * $3,451 where nobody could see it (#335).
 */
export const settingsFromRow = (row) => ({
  configuredAt: row.configured_at,
  offeredForMidTerm: row.offered_for_mid_term,
  ownerApproval: row.owner_approval,
  monthlyRent: dollarsExact(row.monthly_rent_cents),
  monthlyRentSource: row.monthly_rent_source,
  floor: dollarsExact(row.floor_cents),
  floorSource: row.floor_source,
  annualRent: dollarsExact(row.annual_rent_cents),
  deposit: dollarsExact(row.deposit_cents),
  cleaningOwnerMinimum: row.cleaning_owner_minimum,
  cleaningSchedules: row.cleaning_schedules ?? ['CHECKOUT'],
  cleaningPrices: Object.fromEntries(
    Object.entries(row.cleaning_prices_cents ?? {}).map(([k, v]) => [k, dollarsExact(v)])),
  pricingStrategyId: row.pricing_strategy_id,
  pets: {
    ...(row.pets ?? {}),
    feeAmount: dollarsExact(row.pets?.feeAmountCents ?? null),
  },
  /*
    THE LADDER HOLDS WHOLE DOLLARS, AND NOW ITS KEYS SAY SO (#346). Until 2026-10-05 they were
    `yearCents` / `midCents` / `shortCents` and held dollars — a 100× trap for anyone who trusted
    the names. MTR 0058 renames the stored keys; `ladderFromRow` still reads the old ones, so a row
    written before that migration (or by 0046/0047 on a database built from zero) reads the same.
  */
  depositLadder: ladderFromRow(row.deposit_ladder),
  /*
    Utilities. Stored in cents like every other money column and read back as dollars, which is
    what Property setup types and what the pricing engine converts at its own boundary. An absent
    row reads as "nobody has costed this home" — never as zero, which would put "utilities: free"
    on a renter's quote.
  */
  utilities: {
    monthly: dollarsExact(row.utilities?.monthlyCents ?? null),
    cap: dollarsExact(row.utilities?.capCents ?? null),
    covers: row.utilities?.covers ?? [],
  },
  notes: row.notes ?? '',
  /* "Good to know" for renters (migration 0079). A database without the column reads as nothing answered. */
  renterInfo: row.renter_info && typeof row.renter_info === 'object' ? row.renter_info : {},
  seededAt: row.seeded_at,
})

/** The deposit ladder off a row, in dollars, whichever key names the row was written with (#346). */
function ladderFromRow(raw) {
  const d = raw && typeof raw === 'object' ? raw : {}
  const pick = (now, before) => (d[now] !== undefined ? d[now] : d[before]) ?? null
  return {
    yearDollars: pick('yearDollars', 'yearCents'),
    midDollars: pick('midDollars', 'midCents'),
    shortDollars: pick('shortDollars', 'shortCents'),
  }
}

export const settingsToRow = (propertyId, s) => ({
  property_id: propertyId,
  offered_for_mid_term: s.offeredForMidTerm,
  owner_approval: s.ownerApproval,
  monthly_rent_cents: toCents(s.monthlyRent),
  monthly_rent_source: s.monthlyRentSource ?? null,
  floor_cents: toCents(s.floor),
  floor_source: s.floorSource ?? null,
  annual_rent_cents: toCents(s.annualRent),
  deposit_cents: toCents(s.deposit),
  cleaning_owner_minimum: s.cleaningOwnerMinimum,
  cleaning_schedules: s.cleaningSchedules ?? ['CHECKOUT'],
  cleaning_prices_cents: Object.fromEntries(
    Object.entries(s.cleaningPrices ?? {}).map(([k, v]) => [k, toCents(v)])),
  pricing_strategy_id: s.pricingStrategyId ?? null,
  pets: { ...(s.pets ?? {}), feeAmountCents: toCents(s.pets?.feeAmount), feeAmount: undefined },
  deposit_ladder: s.depositLadder ?? {},
  utilities: {
    monthlyCents: toCents(s.utilities?.monthly),
    capCents: toCents(s.utilities?.cap),
    covers: s.utilities?.covers ?? [],
  },
  notes: s.notes ?? '',
  /*
    Written only once something is in it, so a home saved before migration 0079 has run is saved
    exactly as before — the column it would name does not exist yet (the lesson of #431).
  */
  ...(s.renterInfo && Object.keys(s.renterInfo).length ? { renter_info: s.renterInfo } : {}),
  configured_at: s.configuredAt ?? null,
  seeded_at: s.seededAt ?? null,
})

export const extensionFromRow = (row) => ({
  reservationId: row.reservation_id,
  extensionDecision: row.decision,
  askedAt: row.asked_at,
  /*
    ONE NAME FOR THE MOMENT, AND THIS PAIR IS THE ONLY PLACE THE COLUMN NAME APPEARS.

    There used to be two: the screen wrote `extensionDecidedAt` when somebody answered, the
    automation events read `extensionDecidedAt`, and the writer below reached for `decidedAt` —
    a name nothing else in the app ever set. So every saved answer went into the table with
    `decided_at` NULL, came back without a moment, and every rule waiting on "they are extending"
    stayed silent from the next reload on. The answer survived; the event it was supposed to
    cause did not. (Copilot, #41.)
  */
  extensionDecidedAt: row.decided_at,
  newCheckOut: row.new_check_out,
  note: row.note ?? '',
})

export const extensionToRow = (reservationId, e) => ({
  reservation_id: reservationId,
  decision: e.extensionDecision ?? e.decision ?? null,
  asked_at: e.askedAt ?? null,
  decided_at: e.extensionDecidedAt ?? null,
  new_check_out: e.newCheckOut ?? null,
  note: e.note ?? '',
})

/**
 * A report from the floating button, on its way into `mtr.feedback`.
 *
 * `created_by` IS THE FIELD THIS MAPPER EXISTS FOR. The column has been in the schema since the
 * first migration and nothing was ever putting anything in it, so every report would have landed
 * anonymous — which sounds harmless and is not: half of what people write is "this looks wrong"
 * about a number, and the only way to finish that sentence is to go back to the person who saw
 * it. A table of unattributable complaints is a table nobody can act on.
 *
 * It is nullable rather than required because losing the report is worse than losing the name.
 *
 * THE KIND IS A COLUMN NOW, NOT A PREFIX ON THE MESSAGE. It used to be written in as a leading
 * "Broken:" because adding a column belonged to somebody else's lane that week. That made it
 * impossible to filter or count without parsing English, which is the whole of what a periodic
 * read of this table has to do.
 */
export const feedbackToRow = ({
  message, page = null, createdBy = null,
  kind = null, title = null, route = null, pageUrl = null, pageTitle = null, appVersion = null,
} = {}) => ({
  message,
  page: page ?? null,
  created_by: createdBy ?? null,
  /* Since 2026-09-15 these come from the shared report module, so this table and owner
     acquisition's `product_feedback` hold the same shape and one query can read both. Every one
     is nullable: the row written before the module existed has none of them, and a report that
     arrives without a page is still a report. */
  kind: kind ?? null,
  title: title ?? null,
  route: route ?? null,
  page_url: pageUrl ?? null,
  page_title: pageTitle ?? null,
  app_version: appVersion ?? null,
})

/**
 * A row of `mtr.feedback` → a report on the Feedback page (MTR #75).
 *
 * `by` is the person's name, resolved here from the staff list, because a report whose author is
 * a uuid is the same as an anonymous one to whoever reads it. `screenshots` is filled from
 * `feedback_attachments` by the store; a row is never shown with pictures it does not have.
 */
export const feedbackFromRow = (row, staffById = new Map(), attachments = [], comments = []) => ({
  id: row.id,
  reference: row.reference ?? null,
  kind: row.kind ?? null,
  title: row.title ?? null,
  message: row.message ?? '',
  screen: row.page ?? null,
  route: row.route ?? null,
  pageUrl: row.page_url ?? null,
  pageTitle: row.page_title ?? null,
  appVersion: row.app_version ?? null,
  status: row.status ?? 'NEW',
  resolutionSummary: row.resolution_summary ?? null,
  createdBy: row.created_by ?? null,
  by: (row.created_by && staffById.get?.(row.created_by)) || row.reporter_name || null,
  createdAt: row.created_at ?? null,
  updatedAt: row.updated_at ?? null,
  closedAt: row.closed_at ?? null,
  screenshots: attachments.map((a) => ({
    id: a.id, bucket: a.storage_bucket, path: a.storage_path, name: a.file_name, type: a.mime_type,
  })),
  comments: comments.map((c) => feedbackCommentFromRow(c, staffById)),
})

/**
 * A row of `mtr.feedback_comments` → a comment under a report. The name written with it wins (it is
 * the name the person had when they wrote it); the staff list is the fallback for a row without one.
 */
export const feedbackCommentFromRow = (row, staffById = new Map()) => ({
  id: row.id,
  body: row.body ?? '',
  by: row.author_name || (row.author_id && staffById.get?.(row.author_id)) || null,
  createdAt: row.created_at ?? null,
})
